import { signOut } from 'next-auth/react';
import { TextEncoder } from 'text-encoding';
import { logError, logWarning } from 'lib/util/errors';
import { env } from 'lib/env';
import { Session } from 'next-auth';
import { javaHash, uintArrayToHex } from 'lib/auth/core';

export { javaHash } from 'lib/auth/core';

/**
 * Signs user out of next-auth and then redirects to OKTA for federated logout.
 * @param token OKTA id token identifying user to sign out.
 */
export const appSignOut = (token: string) => {
  const baseUrl = process.env.OKTA_ISSUER as string;
  const postLogoutRedirectUri = env.PUBLIC_URL.concat('/');
  const encodedRedirectedUri = encodeURIComponent(postLogoutRedirectUri);
  return signOut().then(() => {
    window.location.href = `${baseUrl}/v1/logout?id_token_hint=${token}&post_logout_redirect_uri=${encodedRedirectedUri}`;
  });
};

/**
 * Represents a type that can be used to define an anonymous user.
 *
 * This type can be one of the following:
 * - `string[]`: An array of strings.
 * - `undefined`: Indicates that the user is not defined.
 * - `null`: Indicates that the user is explicitly set to null.
 * - `string`: A single string value.
 */
type AnonymousUserType = string[] | undefined | null | string;

/**
 * Determines if the provided roles indicate an anonymous user.
 *
 * @param roles - The roles to check. Can be a string or an array of strings.
 * @returns `true` if the roles indicate an anonymous user, otherwise `false`.
 *
 * @example
 * ```typescript
 * const roles = ['user', 'admin'];
 * const isAnon = isAnonymousUser(roles); // false
 *
 * const noRoles = [];
 * const isAnonNoRoles = isAnonymousUser(noRoles); // true
 *
 * const singleRole = ' ';
 * const isAnonSingleRole = isAnonymousUser(singleRole); // true
 * ```
 */
export const isAnonymousUser = (roles: AnonymousUserType): boolean => {
  // If we have no roles we are anon
  if (!roles) {
    return true;
  }
  // Normalize input
  let checkRoles: string[];
  if (roles && Array.isArray(roles)) {
    checkRoles = roles;
  } else {
    checkRoles = [roles.trim()].filter((x) => x.length);
  }
  // If we have no roles or one and only one role that matches the anon role
  if (checkRoles.length === 0) {
    // Then we are anon
    return true;
  }
  // Otherwise we are some kind of user
  return false;
};

/**
 * Checks if the given user is a test user based on their email or name.
 * A test user is identified if their email or name matches the pattern `test\d+` or `l1telerik`.
 *
 * @param {Session['user']} [user] - The user session object containing user details.
 * @returns {boolean} - Returns `true` if the user is a test user, otherwise `false`.
 *
 * @example
 * ```typescript
 * const user1 = { email: 'test123@example.com', name: 'John Doe' };
 * const user2 = { email: 'jane.doe@example.com', name: 'l1telerik' };
 * const user3 = { email: 'user@example.com', name: 'Jane Doe' };
 *
 * logInfo(isTestUser(user1)); // true
 * logInfo(isTestUser(user2)); // true
 * logInfo(isTestUser(user3)); // false
 * ```
 */
export const isTestUser = (user?: Session['user']): boolean =>
  /test\d+|l1telerik/i.test(user?.email ?? '') || /test\d+|l1telerik/i.test(user?.name ?? '');

/**
 * Simple SHA-256 hash that will run on the browser or nodeJS runtime.
 * IMPORTANT: this is not a secure hasher, and should not be used with sensitive data.
 * @param input The input value to hash
 * @returns The hashed result.
 */
export const insecureHash = async (input: string) => {
  // Bounce null and undefined back
  if (input === null || input === undefined) {
    return Promise.resolve(input);
  }
  // Make sure we're running on a browser with crypto WebApi support
  if (typeof window === 'undefined') {
    logError(
      'lib/auth/client-utils',
      new Error(
        'Detected a server-side call to insecureHash; this method should only be used on the client.'
      )
    );
    return javaHash(input);
  }
  if (typeof window.crypto === 'undefined' || typeof window.crypto.subtle === 'undefined') {
    logWarning('crypto WebApi unavailable; falling back to legacy hash');
    return javaHash(input);
  }
  const encoder = new TextEncoder();
  const inputBytes = encoder.encode(input);
  try {
    const hashedBuffer = await window.crypto.subtle.digest('SHA-256', inputBytes as BufferSource);
    // Translate the result into a hex string
    return uintArrayToHex(new Uint8Array(hashedBuffer));
  } catch (e) {
    // If we get here the user's device doesn't support SHA-256.  Fallback to
    // old-school java hash function
    return javaHash(input);
  }
};
